export const PRODUCT_LINE_TIERS = [
  "Cheap",
  "Eco",
  "Standard",
  "Performance",
] as const;
export type ProductLineTier = (typeof PRODUCT_LINE_TIERS)[number];

/**
 * Chip colour per tier, from the status tokens so both themes follow. The hover repeats the fill:
 * the chip is not interactive, and Badge's own hover would otherwise grey it out.
 */
export const TIER_COLORS: Record<ProductLineTier, string> = {
  Cheap: "border-transparent bg-ok-soft text-ok-ink hover:bg-ok-soft",
  Eco: "border-transparent bg-info-soft text-info-ink hover:bg-info-soft",
  Standard:
    "border-transparent bg-brand-soft text-brand-ink hover:bg-brand-soft",
  Performance:
    "border-transparent bg-warn-soft text-warn-ink hover:bg-warn-soft",
};

/**
 * Chip types (CPU architectures) an admin may enable for a product line. Must mirror
 * ProductLine.SupportedChipTypes on the backend. Drives validation and the edit form checkboxes.
 */
export const SUPPORTED_CHIP_TYPES = ["Intel", "AMD"] as const;
export type SupportedChipType = (typeof SUPPORTED_CHIP_TYPES)[number];

export const MAX_SPEC_CONFIGS = 6;

/** Language codes a preset label can be authored in. Mirrors the client app's locale folders. */
export const PRESET_LOCALES = ["en", "vi"] as const;
export type PresetLocale = (typeof PRESET_LOCALES)[number];

export interface SpecConfigDto {
  key: string;
  labels: Record<string, string>;
  descriptions?: Record<string, string>;
  cores: number;
  memoryGb: number;
  diskGb: number;
  additionalIps: number;
  backupCount: number;
  snapshotCount: number;
  isDefault: boolean;
  sortOrder: number;
}

export interface SpecRangeDto {
  min: number;
  max: number;
  step: number;
}

export interface SpecLimitsDto {
  cores?: SpecRangeDto | null;
  memoryGb?: SpecRangeDto | null;
  diskGb?: SpecRangeDto | null;
  ips?: SpecRangeDto | null;
  backupCount?: SpecRangeDto | null;
  snapshotCount?: SpecRangeDto | null;
}

export interface ProductLineListDto {
  id: string;
  tier: ProductLineTier;
  name: string;
  slug: string;
  pricePerCpuCore: number;
  pricePerRamGb: number;
  pricePerSsdGb: number;
  isActive: boolean;
  sortOrder: number;
  productCount: number;
  createdAt: string;
  modifiedAt?: string;
}

export interface ProductLineDetailDto extends ProductLineListDto {
  description?: string;
  priceFirstIp: number;
  priceAdditionalIp: number;
  pricePerBackupGb: number;
  pricePerSnapshotGb: number;
  defaultBackupCount: number;
  defaultSnapshotCount: number;
  /** Raw metadata JSON. Nullable on the backend (`string?`), unlike the price DTO's. */
  metadata?: string;
  enabledChipTypes?: string[];
  /**
   * Provider pool each chip type is placed in, keyed by chip type. Overrides the environment's
   * WiService pool mappings; a chip absent from it falls back to `fallbackPoolNames`.
   */
  poolNames?: Record<string, string>;
  /**
   * Read-only: the pool each chip type resolves to with no override configured. Shown as the
   * placeholder so an operator can see what clearing a box restores.
   */
  fallbackPoolNames?: Record<string, string>;
  /**
   * Share of its cores a VM on this line may use on the host (0 < ratio <= 1). Null when the line
   * stores none and the default (0.7) applies.
   */
  cpuLimitRatio?: number | null;
  /**
   * Port speed in MB/s a VM on this line is created with (0 < rate <= 10000). Null when the line
   * stores none and the default (13) applies.
   */
  networkRate?: number | null;
  specConfigs: SpecConfigDto[];
  specLimits?: SpecLimitsDto | null;
  skuCount: number;
}

export interface CreateProductLineRequest {
  tier: string;
  name: string;
  slug: string;
  description?: string;
  pricePerCpuCore: number;
  pricePerRamGb: number;
  pricePerSsdGb: number;
  priceFirstIp: number;
  priceAdditionalIp: number;
  pricePerBackupGb: number;
  pricePerSnapshotGb: number;
  defaultBackupCount: number;
  defaultSnapshotCount: number;
  sortOrder: number;
  metadata?: string;
  enabledChipTypes?: string[];
  poolNames?: Record<string, string>;
  cpuLimitRatio?: number;
  networkRate?: number;
  specConfigs?: SpecConfigDto[];
  specLimits?: SpecLimitsDto | null;
}

export interface UpdateProductLineRequest {
  name?: string;
  slug?: string;
  description?: string;
  pricePerCpuCore?: number;
  pricePerRamGb?: number;
  pricePerSsdGb?: number;
  priceFirstIp?: number;
  priceAdditionalIp?: number;
  pricePerBackupGb?: number;
  pricePerSnapshotGb?: number;
  defaultBackupCount?: number;
  defaultSnapshotCount?: number;
  isActive?: boolean;
  sortOrder?: number;
  metadata?: string;
  enabledChipTypes?: string[];
  poolNames?: Record<string, string>;
  cpuLimitRatio?: number;
  networkRate?: number;
  specConfigs?: SpecConfigDto[];
  specLimits?: SpecLimitsDto | null;
  clearSpecLimits?: boolean;
}

export interface ProductLineFilters {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export const SUPPORTED_REGIONS = [
  "sg-1",
  "vn-south-1",
  "vn-north-1",
  "vn-central-1",
  "ap-southeast-1",
  "us-east-1",
  "us-west-2",
  "eu-central-1",
] as const;
export type SupportedRegion = (typeof SUPPORTED_REGIONS)[number];

/**
 * The subset of SUPPORTED_REGIONS an order can actually be placed in. Orders are stamped VND, and
 * the pricing engine derives its currency from the item's region, so a region billed in USD or EUR
 * is rejected server-side with UNSUPPORTED_ORDER_CURRENCY. Offering one here would only produce a
 * 400 the operator cannot act on.
 *
 * SUPPORTED_REGIONS keeps the full list because product lines still configure region-specific
 * pricing for regions nobody can order in yet.
 */
export const ORDERABLE_REGIONS = [
  "sg-1",
  "vn-south-1",
  "vn-north-1",
  "vn-central-1",
  "ap-southeast-1",
] as const satisfies readonly SupportedRegion[];
export type OrderableRegion = (typeof ORDERABLE_REGIONS)[number];

export const SUPPORTED_CURRENCIES = [
  "VND",
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "KWD",
] as const;
export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

export const BILLING_FREQUENCIES = [
  "Hourly",
  "Daily",
  "Monthly",
  "Quarterly",
  "Annually",
] as const;
export type BillingFrequency = (typeof BILLING_FREQUENCIES)[number];

export const MAX_BACKUP_SNAPSHOT_COUNT = 7;

export interface ProductLinePriceListDto {
  id: string;
  productLineId: string;
  region?: string | null;
  currency: string;
  billingFrequency: string;
  pricePerCpuCore: number;
  pricePerRamGb: number;
  pricePerSsdGb: number;
  priceFirstIp: number;
  priceAdditionalIp: number;
  pricePerBackupGb: number;
  pricePerSnapshotGb: number;
  defaultBackupCount: number;
  defaultSnapshotCount: number;
  isActive: boolean;
  createdAt: string;
  modifiedAt?: string;
}

export interface ProductLinePriceDetailDto extends ProductLinePriceListDto {
  metadata: string;
}

export interface CreateProductLinePriceRequest {
  region?: string | null;
  currency: string;
  billingFrequency: string;
  pricePerCpuCore: number;
  pricePerRamGb: number;
  pricePerSsdGb: number;
  priceFirstIp: number;
  priceAdditionalIp: number;
  pricePerBackupGb: number;
  pricePerSnapshotGb: number;
  defaultBackupCount: number;
  defaultSnapshotCount: number;
  metadata?: string;
}

export interface UpdateProductLinePriceRequest {
  pricePerCpuCore?: number;
  pricePerRamGb?: number;
  pricePerSsdGb?: number;
  priceFirstIp?: number;
  priceAdditionalIp?: number;
  pricePerBackupGb?: number;
  pricePerSnapshotGb?: number;
  defaultBackupCount?: number;
  defaultSnapshotCount?: number;
  billingFrequency?: string;
  isActive?: boolean;
  metadata?: string;
}

export interface ProductLinePriceFilters {
  region?: string;
  currency?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}
